import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

// The rule-based linker reaches the videos that spell a store the way the store
// master holds it. Everything else lands here: a model read a store name out of
// the title and the description, and a person says yes or no. The link is only
// written on a yes — a wrong store on a store page is worse than a missing one.

const PAGE_SIZE = 30;

const STATUSES = [
  { value: 'pending', label: '未判定' },
  { value: 'confirmed', label: '紐付けた' },
  { value: 'rejected', label: '読み取りが誤り' },
  { value: 'no_store', label: '店舗マスタに無い' },
  { value: 'all', label: 'すべて' },
];

const RESOLVED = [
  { value: '', label: 'すべて' },
  { value: 'matched', label: '候補店舗あり' },
  { value: 'missing', label: '候補店舗なし' },
];

const CONFIDENCE = {
  high: { label: '確度 高', className: 'bg-success-subtle text-success-emphasis' },
  medium: { label: '確度 中', className: 'bg-warning-subtle text-warning-emphasis' },
  low: { label: '確度 低', className: 'bg-secondary-subtle text-secondary-emphasis' },
};

export default function VideoSuggestions() {
  useAuthGuard();

  const [status, setStatus] = useState('pending');
  const [resolved, setResolved] = useState('');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [missing, setMissing] = useState(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const url = new URL(`${API_BASE_URL}/admin/videos/suggestions`);
      url.searchParams.set('status', status);
      url.searchParams.set('limit', String(PAGE_SIZE));
      url.searchParams.set('offset', String(offset));
      if (resolved) url.searchParams.set('resolved', resolved);

      const res = await apiFetch(url.toString(), { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems(Array.isArray(body.data) ? body.data : []);
      setTotal(body.meta?.total || 0);
      setCounts(body.meta?.counts || {});
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Network Error');
    } finally {
      setLoading(false);
    }
  }, [status, resolved, offset]);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const loadMissing = async () => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/videos/suggestions/missing-stores?limit=60`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setMissing(Array.isArray(body.data) ? body.data : []);
    } catch (e) {
      setError(e.message || 'Network Error');
    }
  };

  // A decided row leaves the queue immediately: waiting for a refetch would
  // make the operator lose their place every few clicks.
  const decide = async (item, nextStatus, storeId) => {
    setBusyId(item.id);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/videos/suggestions/${item.id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus, storeId: storeId || undefined }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      setTotal((t) => Math.max(0, t - 1));
      setCounts((c) => ({
        ...c,
        pending: Math.max(0, (c.pending || 0) - 1),
        [nextStatus]: (c[nextStatus] || 0) + 1,
      }));
    } catch (e) {
      setError(e.message || 'Network Error');
    } finally {
      setBusyId(null);
    }
  };

  const progress = useMemo(() => {
    const decided = (counts.confirmed || 0) + (counts.rejected || 0) + (counts.no_store || 0);
    return { decided, pending: counts.pending || 0 };
  }, [counts]);

  return (
    <Layout>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-1">動画と店舗の紐付け確認</h4>
          <p className="text-muted mb-0 small">
            規則で紐付かなかった動画から、AIが読み取った店名を確認して確定します。
            「紐付ける」を押したときだけ実際に紐付きます。
          </p>
        </div>
        <div className="text-end small text-muted">
          <div>判定済み <strong>{progress.decided}</strong> / 未判定 <strong>{progress.pending}</strong></div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-3 align-items-end">
          <div>
            <label className="form-label small mb-1">状態</label>
            <select
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
            >
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">候補店舗</label>
            <select
              className="form-select form-select-sm"
              value={resolved}
              onChange={(e) => { setResolved(e.target.value); setOffset(0); }}
            >
              {RESOLVED.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadMissing}>
            マスタに無い店名を見る
          </button>
        </div>
      </div>

      {missing && (
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">店舗マスタに無い店名（動画本数順）</h6>
              <button type="button" className="btn-close" onClick={() => setMissing(null)} aria-label="閉じる" />
            </div>
            <p className="text-muted small">次に収集すべき店の一覧です。動画が待っている順に並んでいます。</p>
            {missing.length === 0 ? (
              <p className="text-muted small mb-0">ありません。</p>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {missing.map((m) => (
                  <span key={m.extractedName} className="badge bg-light text-dark border">
                    {m.extractedName} <span className="text-muted">×{m.videoCount}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {loading ? (
        <div className="text-muted">読み込み中…</div>
      ) : items.length === 0 ? (
        <div className="alert alert-success">この条件の未判定はありません。</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {items.map((item) => (
            <SuggestionCard
              key={item.id}
              item={item}
              busy={busyId === item.id}
              onDecide={decide}
            />
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            前へ
          </button>
          <span className="small text-muted">{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} / {total}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            次へ
          </button>
        </div>
      )}
    </Layout>
  );
}

function SuggestionCard({ item, busy, onDecide }) {
  const [manualStoreId, setManualStoreId] = useState('');
  const conf = CONFIDENCE[item.confidence] || CONFIDENCE.low;
  const watchUrl = item.videoId ? `https://www.youtube.com/watch?v=${item.videoId}` : null;

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex gap-3">
          {item.videoThumbnail && (
            <img
              src={item.videoThumbnail}
              alt=""
              width={160}
              className="rounded flex-shrink-0"
              style={{ objectFit: 'cover', height: 90 }}
            />
          )}
          <div className="flex-grow-1 min-width-0">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className={`badge ${conf.className}`}>{conf.label}</span>
              {watchUrl && (
                <a href={watchUrl} target="_blank" rel="noreferrer" className="small">
                  YouTubeで開く
                </a>
              )}
            </div>
            <div className="fw-semibold text-truncate" title={item.videoTitle}>{item.videoTitle}</div>

            <div className="mt-2 small">
              <div>
                AIが読み取った店名: <strong>{item.extractedName}</strong>
              </div>
              {item.evidence && (
                <div className="text-muted">根拠: 「{item.evidence}」</div>
              )}
            </div>

            <div className="mt-2 small">
              {item.storeId ? (
                <div>
                  候補店舗: <strong>{item.storeName}</strong>
                  <span className="text-muted"> — {item.storePrefecture} {item.storeAddress}</span>
                </div>
              ) : (
                <div className="text-warning-emphasis">
                  この店名に一致する店舗が店舗マスタにありません。
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
          {item.storeId && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={busy}
              onClick={() => onDecide(item, 'confirmed')}
            >
              この店舗に紐付ける
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={busy}
            onClick={() => onDecide(item, 'no_store')}
          >
            店名は正しいがマスタに無い
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            disabled={busy}
            onClick={() => onDecide(item, 'rejected')}
          >
            読み取りが誤り
          </button>

          <div className="ms-auto d-flex gap-2 align-items-center">
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ width: 300 }}
              placeholder="別の店舗ID を指定して紐付ける"
              value={manualStoreId}
              onChange={(e) => setManualStoreId(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={busy || !manualStoreId.trim()}
              onClick={() => onDecide(item, 'confirmed', manualStoreId.trim())}
            >
              指定して紐付け
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
